import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CheckCircle2, Sparkles } from 'lucide-react'
import {
  createExtractionProfile,
  extractionValueTypeLabels,
  fetchBusinessEntities,
  fetchDivisions,
  fetchPcrFieldCoverage,
  fetchProducts,
  fetchSubdivisions,
  updateExtractionProfileStatus,
  type ExtractionValueTypeLabel,
  type PcrFieldCoverageDto,
} from '../services/api'
import { EmptyState, MimCard, MimPage } from '../components/MimUi'

type GeneratedFieldRow = {
  canonicalFieldKey: string
  displayName: string
  valueType: ExtractionValueTypeLabel
  unit: string
  requiredOnWebsite: boolean
  claimCheckRole: string
  recordsWithData: number
  totalRecords: number
  included: boolean
}

const claimRoleOptions = [
  { value: 'OutOfScope', label: 'Out of scope' },
  { value: 'MustNotContradict', label: 'Must not contradict' },
  { value: 'MustMatch', label: 'Must match' },
]

type ExtractionProfileGenerateProps = {
  embedded?: boolean
  productId?: string
  onComplete?: () => void
}

function toRow(field: PcrFieldCoverageDto): GeneratedFieldRow {
  const claimCheckRole = field.suggestedClaimCheckRole || 'OutOfScope'
  return {
    canonicalFieldKey: field.canonicalFieldKey,
    displayName: field.displayName || field.canonicalFieldKey,
    valueType: field.suggestedValueType,
    unit: field.suggestedUnit || '',
    requiredOnWebsite: claimCheckRole === 'MustMatch',
    claimCheckRole,
    recordsWithData: field.recordsWithData,
    totalRecords: field.totalRecords,
    included: claimCheckRole !== 'OutOfScope' && field.recordsWithData > 0,
  }
}

export function ExtractionProfileGenerate({
  embedded = false,
  productId: productIdProp,
  onComplete,
}: ExtractionProfileGenerateProps) {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [searchParams] = useSearchParams()
  const scopedProductId = productIdProp || searchParams.get('productId') || ''
  const productScoped = !!scopedProductId

  const [name, setName] = useState('Generated from PCR data')
  const [version, setVersion] = useState('1.0.0')
  const [divisionId, setDivisionId] = useState('')
  const [subdivisionId, setSubdivisionId] = useState('')
  const [businessEntityId, setBusinessEntityId] = useState('')
  const [totalApprovedPcrCount, setTotalApprovedPcrCount] = useState<number | null>(null)
  const [rows, setRows] = useState<GeneratedFieldRow[]>([])
  const [message, setMessage] = useState('')
  const [error, setError] = useState('')
  const [createdProfileId, setCreatedProfileId] = useState<string | null>(null)

  const { data: products = [] } = useQuery({
    queryKey: ['products', 'extraction-profile-generate'],
    queryFn: () => fetchProducts({ kingspanOnly: true }),
    enabled: productScoped,
  })
  const scopedProduct = products.find((product) => product.id === scopedProductId)

  const { data: divisions = [] } = useQuery({
    queryKey: ['org', 'divisions'],
    queryFn: fetchDivisions,
    enabled: !productScoped,
  })
  const { data: subdivisions = [] } = useQuery({
    queryKey: ['org', 'subdivisions', divisionId],
    queryFn: () => fetchSubdivisions(divisionId ? { divisionId } : undefined),
    enabled: !productScoped,
  })
  const { data: businessEntities = [] } = useQuery({
    queryKey: ['org', 'business-entities', subdivisionId],
    queryFn: () => fetchBusinessEntities(subdivisionId ? { subdivisionId } : undefined),
    enabled: !productScoped,
  })

  const clearFeedback = () => {
    setMessage('')
    setError('')
  }

  const analyzeMutation = useMutation({
    mutationFn: () =>
      fetchPcrFieldCoverage(
        productScoped
          ? { productId: scopedProductId }
          : {
              divisionId: divisionId || undefined,
              subdivisionId: subdivisionId || undefined,
              businessEntityId: businessEntityId || undefined,
            },
      ),
    onSuccess: (data) => {
      setTotalApprovedPcrCount(data.totalApprovedPcrCount)
      setRows(data.fields.map(toRow))
      setMessage(
        data.totalApprovedPcrCount === 0
          ? 'No approved PCRs found in this scope. Approve some PCR data first, or broaden the scope.'
          : `Found ${data.totalApprovedPcrCount} approved PCR${data.totalApprovedPcrCount === 1 ? '' : 's'} in this scope with ${data.fields.length} distinct field${data.fields.length === 1 ? '' : 's'}. Fields with no data are unchecked - tick them back on if you still want them.`,
      )
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to analyze PCR field coverage.')),
  })

  useEffect(() => {
    if (!productScoped || totalApprovedPcrCount !== null) return
    analyzeMutation.mutate()
  // eslint-disable-next-line react-hooks/exhaustive-deps -- auto-analyze once per scoped product
  }, [productScoped, scopedProductId])

  const createMutation = useMutation({
    mutationFn: () =>
      createExtractionProfile({
        name: name.trim(),
        version: version.trim(),
        divisionId: productScoped ? undefined : divisionId || undefined,
        subdivisionId: productScoped ? undefined : subdivisionId || undefined,
        businessEntityId: productScoped ? undefined : businessEntityId || undefined,
        fields: rows
          .filter((row) => row.included)
          .map((row) => ({
            canonicalFieldKey: row.canonicalFieldKey,
            displayName: row.displayName.trim() || undefined,
            valueType: row.valueType,
            unit: row.unit.trim() || undefined,
            requiredOnWebsite: row.claimCheckRole === 'MustMatch',
            claimCheckRole: row.claimCheckRole,
          })),
      }),
    onSuccess: async (created) => {
      setCreatedProfileId(created.id)
      await qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] })
      if (embedded) {
        try {
          await updateExtractionProfileStatus(created.id, 'Approved')
        } catch (err) {
          setError(getErrorMessage(err, 'Profile created but approval failed. Use Approve profile now.'))
          return
        }
        await Promise.all([
          qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] }),
          qc.invalidateQueries({ queryKey: ['compliance-preflight'] }),
        ])
        setMessage(`Extraction report ${created.name} approved.`)
        onComplete?.()
        return
      }
      navigate(`/extraction-profiles/${created.id}`)
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to create extraction profile.')),
  })

  const approveMutation = useMutation({
    mutationFn: (profileId: string) => updateExtractionProfileStatus(profileId, 'Approved'),
    onSuccess: async () => {
      await Promise.all([
        qc.invalidateQueries({ queryKey: ['extraction-profiles', 'rules-engine'] }),
        qc.invalidateQueries({ queryKey: ['compliance-preflight'] }),
      ])
      setMessage('Extraction profile approved. Compliance run preflight should now pass for matching products.')
      onComplete?.()
    },
    onError: (err) => setError(getErrorMessage(err, 'Failed to approve extraction profile.')),
  })

  const includedCount = useMemo(() => rows.filter((row) => row.included).length, [rows])

  const handleAnalyze = () => {
    clearFeedback()
    setCreatedProfileId(null)
    analyzeMutation.mutate()
  }

  const handleCreate = () => {
    clearFeedback()
    if (!name.trim() || !version.trim()) {
      setError('Name and version are required.')
      return
    }
    if (includedCount === 0) {
      setError('Include at least one field.')
      return
    }
    createMutation.mutate()
  }

  const updateRow = (key: string, patch: Partial<GeneratedFieldRow>) => {
    setRows((current) => current.map((row) => (row.canonicalFieldKey === key ? { ...row, ...patch } : row)))
  }

  const body = (
    <div className="mim-stack">
      {message && <div className="rules-feedback rules-feedback-success">{message}</div>}
      {error && <div className="rules-feedback rules-feedback-error">{error}</div>}

      <MimCard
        title={productScoped ? '1. Product scope' : '1. Scope'}
        subtitle={productScoped
          ? 'Analyzing approved PCR field coverage for this product only.'
          : 'Narrow this to a Division, Subdivision, or Business Entity to only analyze PCRs in that scope - or leave it open to scan every approved PCR.'}
      >
        {productScoped ? (
          <div className="mim-stack">
            <span className="mim-help-text">
              Product: <strong>{scopedProduct ? `${scopedProduct.name} (${scopedProduct.code})` : scopedProductId}</strong>
            </span>
            <div className="mim-toolbar">
              <button type="button" className="mim-btn mim-btn-primary" disabled={analyzeMutation.isPending} onClick={handleAnalyze}>
                <Sparkles size={14} /> {analyzeMutation.isPending ? 'Analyzing...' : 'Re-analyze PCR data'}
              </button>
            </div>
          </div>
        ) : (
          <>
            <div className="mim-grid-3">
              <label>
                <span className="mim-label">Division</span>
                <select
                  className="mim-select"
                  value={divisionId}
                  onChange={(e) => { setDivisionId(e.target.value); setSubdivisionId(''); setBusinessEntityId('') }}
                >
                  <option value="">Any division</option>
                  {divisions.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
                </select>
              </label>
              <label>
                <span className="mim-label">Subdivision</span>
                <select
                  className="mim-select"
                  value={subdivisionId}
                  onChange={(e) => { setSubdivisionId(e.target.value); setBusinessEntityId('') }}
                >
                  <option value="">Any subdivision</option>
                  {subdivisions.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label>
                <span className="mim-label">Business entity</span>
                <select
                  className="mim-select"
                  value={businessEntityId}
                  onChange={(e) => setBusinessEntityId(e.target.value)}
                >
                  <option value="">Any business entity</option>
                  {businessEntities.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </label>
            </div>
            <div className="mim-toolbar" style={{ marginTop: 12 }}>
              <button type="button" className="mim-btn mim-btn-primary" disabled={analyzeMutation.isPending} onClick={handleAnalyze}>
                <Sparkles size={14} /> {analyzeMutation.isPending ? 'Analyzing...' : 'Analyze PCR data'}
              </button>
            </div>
          </>
        )}
      </MimCard>

      {totalApprovedPcrCount !== null && totalApprovedPcrCount > 0 && (
        <>
          <MimCard title="2. Name the profile">
            <div className="mim-grid-2">
              <label>
                <span className="mim-label">Name *</span>
                <input className="mim-input" value={name} onChange={(e) => setName(e.target.value)} />
              </label>
              <label>
                <span className="mim-label">Version *</span>
                <input className="mim-input" value={version} onChange={(e) => setVersion(e.target.value)} placeholder="1.0.0" />
              </label>
            </div>
          </MimCard>

          <MimCard
            title="3. Review suggested fields"
            subtitle="Marketing claims are ticked by default. Register columns such as market flags stay out of scope until you promote them. Must match means the page has to state the value. Must not contradict means silence is fine and a conflicting value is a finding."
            actions={(
              <div className="mim-toolbar">
                <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setRows((current) => current.map((r) => ({ ...r, included: r.claimCheckRole !== 'OutOfScope' && r.recordsWithData > 0 })))}>
                  Marketing claims
                </button>
                <button type="button" className="mim-btn mim-btn-secondary" onClick={() => setRows((current) => current.map((r) => ({ ...r, included: false })))}>
                  Clear all
                </button>
              </div>
            )}
          >
            {rows.length === 0 ? (
              <EmptyState title="No fields found" detail="No approved PCR has any normalized fields yet." />
            ) : (
              <div className="mim-table-wrap">
                <table className="mim-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>Canonical Key</th>
                      <th>Display Name</th>
                      <th>Coverage</th>
                      <th>Value Type</th>
                      <th>Unit</th>
                      <th>Check</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((row) => {
                      const pct = row.totalRecords > 0 ? Math.round((row.recordsWithData / row.totalRecords) * 100) : 0
                      return (
                        <tr key={row.canonicalFieldKey} style={{ opacity: row.included ? 1 : 0.5 }}>
                          <td>
                            <input
                              type="checkbox"
                              checked={row.included}
                              onChange={(e) => updateRow(row.canonicalFieldKey, { included: e.target.checked })}
                            />
                          </td>
                          <td><span className="mim-mono">{row.canonicalFieldKey}</span></td>
                          <td>
                            <input
                              className="mim-input"
                              value={row.displayName}
                              disabled={!row.included}
                              onChange={(e) => updateRow(row.canonicalFieldKey, { displayName: e.target.value })}
                              style={{ minWidth: 160 }}
                            />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 48, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${pct}%`, height: 6, background: pct === 0 ? '#d1d5db' : pct < 30 ? '#f59e0b' : '#22c55e' }} />
                              </div>
                              <span className="mim-note" style={{ margin: 0 }}>{row.recordsWithData} / {row.totalRecords}</span>
                            </div>
                          </td>
                          <td>
                            <select
                              className="mim-select"
                              value={row.valueType}
                              disabled={!row.included}
                              onChange={(e) => updateRow(row.canonicalFieldKey, { valueType: e.target.value as ExtractionValueTypeLabel })}
                            >
                              {extractionValueTypeLabels.map((type) => <option key={type} value={type}>{type}</option>)}
                            </select>
                          </td>
                          <td>
                            <input
                              className="mim-input"
                              value={row.unit}
                              disabled={!row.included}
                              onChange={(e) => updateRow(row.canonicalFieldKey, { unit: e.target.value })}
                              placeholder="-"
                              style={{ minWidth: 70 }}
                            />
                          </td>
                          <td>
                            <select
                              className="mim-select"
                              value={row.claimCheckRole}
                              disabled={!row.included}
                              onChange={(e) => updateRow(row.canonicalFieldKey, {
                                claimCheckRole: e.target.value,
                                requiredOnWebsite: e.target.value === 'MustMatch',
                              })}
                            >
                              {claimRoleOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
                            </select>
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
                <div className="mim-table-footer">{includedCount} of {rows.length} fields selected</div>
              </div>
            )}
            <div className="mim-toolbar" style={{ marginTop: 12 }}>
              {!createdProfileId ? (
                <button type="button" className="mim-btn mim-btn-primary" disabled={createMutation.isPending || includedCount === 0} onClick={handleCreate}>
                  {createMutation.isPending
                    ? 'Creating...'
                    : embedded
                      ? `Create and approve ${includedCount} field${includedCount === 1 ? '' : 's'}`
                      : `Create draft profile with ${includedCount} field${includedCount === 1 ? '' : 's'}`}
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="mim-btn mim-btn-primary"
                    disabled={approveMutation.isPending}
                    onClick={() => approveMutation.mutate(createdProfileId)}
                  >
                    <CheckCircle2 size={14} /> {approveMutation.isPending ? 'Approving...' : 'Approve profile now'}
                  </button>
                  {!embedded && (
                    <Link className="mim-btn mim-btn-secondary" to={`/extraction-profiles/${createdProfileId}`}>Open profile detail</Link>
                  )}
                </>
              )}
            </div>
          </MimCard>
        </>
      )}

      {totalApprovedPcrCount === 0 && !analyzeMutation.isPending && productScoped && (
        <EmptyState
          title="Approve PCRs first"
          detail="This product needs at least one approved PCR before an extraction profile can be generated from its field coverage."
        />
      )}
    </div>
  )

  if (embedded) return body

  return (
    <MimPage
      title="Generate Extraction Profile from PCR data"
      subtitle="Scans your approved PCR records and suggests the fields to extract from websites, based on what data actually exists - so you don't have to type each field by hand."
      actions={<button type="button" className="mim-btn mim-btn-secondary" onClick={() => navigate('/rules-engine')}>&larr; Back to Rules Engine</button>}
    >
      {body}
    </MimPage>
  )
}

export default function ExtractionProfileGeneratePage() {
  return <ExtractionProfileGenerate />
}

function getErrorMessage(err: unknown, fallback: string) {
  const responseData = (err as { response?: { data?: unknown } })?.response?.data
  if (typeof responseData === 'string') return responseData
  if (responseData && typeof responseData === 'object' && 'message' in responseData) {
    const message = (responseData as { message?: unknown }).message
    if (typeof message === 'string') return message
  }
  const message = (err as { message?: unknown })?.message
  return typeof message === 'string' ? message : fallback
}
